import { Building2, Calendar, Eye, Send, Check } from 'lucide-react';
import React from 'react';

interface MobileHistoryCardProps {
  jobTitle: string;
  company: string;
  date: string;
  originalScore: number;
  optimizedScore: number;
  applied: boolean;
  locale?: string;
  onPreview?: () => void;
  onApply?: () => void;
}

/* Theme-aware: use CSS variables so dark mode gets single accent */
function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--vscode-success)';
  return 'var(--vscode-primary)';
}

function getScoreBg(score: number): string {
  if (score >= 80) return 'var(--vscode-success-bg)';
  return 'var(--brand-accent-bg)';
}

/**
 * Native-optimised history card — brand-aligned design
 */
const MobileHistoryCard: React.FC<MobileHistoryCardProps> = ({
  jobTitle,
  company,
  date,
  originalScore,
  optimizedScore,
  applied,
  locale = 'en',
  onPreview,
  onApply,
}) => {
  const scoreColor = getScoreColor(optimizedScore);
  const scoreBg    = getScoreBg(optimizedScore);
  const pct    = Math.max(0, Math.min(100, optimizedScore));
  const origPct = Math.max(0, Math.min(100, originalScore));
  const delta  = optimizedScore - (originalScore ?? 0);
  const dateStr = date
    ? new Date(date).toLocaleDateString(locale, { month: 'short', day: 'numeric' })
    : '—';

  /* SVG donut ring */
  const R     = 21;
  const CIRC  = 2 * Math.PI * R;
  const offset = CIRC - (pct / 100) * CIRC;

  return (
    <div
      style={{
        background: 'var(--vscode-bg-elevated)',
        borderRadius: 18,
        border: '1px solid var(--vscode-border-primary)',
        marginBottom: 10,
        overflow: 'hidden',
        boxShadow: 'var(--vscode-shadow-sm)',
      }}
    >
      <div style={{ height: 3, background: 'var(--vscode-border-primary)' }} />

      <div style={{ padding: '14px 14px 12px' }}>
        {/* Row 1: job info + ATS score ring */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
          {/* Text block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--vscode-text-primary, #111)',
                marginBottom: 5,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {jobTitle || '—'}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <Building2
                size={11}
                style={{ color: 'var(--vscode-text-tertiary, #888)', flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: 12,
                  color: 'var(--vscode-text-secondary, #555)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 130,
                  fontWeight: 500,
                }}
              >
                {company || '—'}
              </span>
              <span style={{ color: 'var(--vscode-border-primary)', fontSize: 10 }}>·</span>
              <Calendar size={11} style={{ color: 'var(--vscode-text-tertiary, #888)', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: 'var(--vscode-text-tertiary, #888)' }}>
                {dateStr}
              </span>
            </div>
          </div>

          {/* ATS score ring */}
          <div
            style={{
              position: 'relative',
              width: 56,
              height: 56,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: scoreBg,
              borderRadius: '50%',
            }}
          >
            <svg width={56} height={56} viewBox="0 0 56 56" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx={28} cy={28} r={R} fill="none" stroke="var(--vscode-border-primary, #e5e5e5)" strokeWidth={4} />
              <circle
                cx={28} cy={28} r={R} fill="none"
                strokeWidth={4}
                strokeDasharray={CIRC}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ stroke: scoreColor, transition: 'stroke-dashoffset 0.6s ease' }}
              />
            </svg>
            <div
              style={{
                position: 'absolute',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0,
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>
                {optimizedScore}
              </span>
              <span style={{ fontSize: 7, fontWeight: 700, color: scoreColor, lineHeight: 1, marginTop: 1, opacity: 0.75, letterSpacing: '0.04em' }}>
                ATS
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: progress bar */}
        <div style={{ marginBottom: 13 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
            }}
          >
            <span style={{ fontSize: 11, color: 'var(--vscode-text-tertiary, #888)', fontWeight: 500 }}>
              {originalScore} <span style={{ opacity: 0.5 }}>→</span> {optimizedScore}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: delta >= 0 ? 'var(--vscode-success)' : 'var(--vscode-error)',
                background: delta >= 0 ? 'var(--vscode-success-bg)' : 'var(--vscode-error-bg)',
                padding: '2px 8px',
                borderRadius: 20,
              }}
            >
              {delta > 0 ? '+' : ''}{delta} pts
            </span>
          </div>
          {/* Track with two fills: original (gray) then improvement (brand) */}
          <div style={{ height: 6, borderRadius: 4, background: 'var(--vscode-progress-bg)', overflow: 'hidden', position: 'relative' }}>
            <div
              style={{
                position: 'absolute', left: 0, top: 0, height: '100%',
                borderRadius: 4,
                width: `${origPct}%`,
                background: 'var(--vscode-border-primary)',
              }}
            />
            <div
              style={{
                position: 'absolute', left: 0, top: 0, height: '100%',
                borderRadius: 4,
                width: `${pct}%`,
                background: 'var(--vscode-progress-fill)',
                transition: 'width 0.6s ease',
              }}
            />
          </div>
        </div>

        {/* Row 3: action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          {onPreview && (
            <button
              type="button"
              onClick={onPreview}
              style={{
                flex: 1,
                minHeight: 42,
                borderRadius: 12,
                border: '1.5px solid var(--ggj-accent-solid, var(--vscode-primary))',
                background: 'var(--brand-accent-bg)',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--ggj-accent-solid, var(--vscode-primary))',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <Eye size={14} />
              Preview
            </button>
          )}

          {applied ? (
            <div
              style={{
                flex: 1,
                minHeight: 42,
                borderRadius: 12,
                background: 'var(--vscode-success-bg)',
                border: '1.5px solid var(--vscode-success-border)',
                color: 'var(--vscode-success)',
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <Check size={14} />
              Applied
            </div>
          ) : onApply ? (
            <button
              type="button"
              onClick={onApply}
              style={{
                flex: 1,
                minHeight: 42,
                borderRadius: 12,
                background: 'var(--ggj-header-bg)',
                border: '1px solid transparent',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--ggj-header-text, #fff)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                WebkitTapHighlightColor: 'transparent',
                boxShadow: 'var(--ggj-btn-shadow, 0 4px 16px rgba(139, 128, 249, 0.3))',
              }}
            >
              <Send size={13} />
              Apply Now
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MobileHistoryCard;
